/**
 * Hook + composant de pagination cursor-based.
 *
 * L'API Kollectivia paginé en cursor opaque (base64url) — voir lib/list.ts
 * côté backend. Pas de page count total, pas d'offset (volontaire : perf
 * stable à grande échelle, voir 0015 indexes).
 *
 * Pour exposer une UI "Précédent/Suivant" classique malgré un cursor pur
 * forward-only, on maintient côté client une pile des cursors visités.
 *
 *   [null, "c1", "c2", "c3", ...]
 *    ^page0  ^page1 ^page2 ^page3
 *
 * - Précédent : pop le top → cursor précédent.
 * - Suivant   : push next_cursor reçu de la dernière query.
 *
 * On ne pré-calcule pas le nombre de pages : l'UI affiche juste
 * "Page N" + boutons. C'est délibéré et conforme à la décision PO MVP.
 *
 * Usage :
 *
 *   const pagination = usePagination();
 *   const { data } = useQuery(['companies', pagination.cursor], () =>
 *     listCompanies({ cursor: pagination.cursor }));
 *
 *   <Pagination
 *     pageNumber={pagination.pageNumber}
 *     hasPrev={pagination.hasPrev}
 *     hasNext={!!data?.next_cursor}
 *     onPrev={pagination.prev}
 *     onNext={() => pagination.next(data?.next_cursor ?? null)}
 *   />
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export type PaginationState = {
  /** Cursor courant (null = première page). */
  cursor: string | null;
  /** Numéro de page 1-indexed (pour affichage). */
  pageNumber: number;
  hasPrev: boolean;
  /** Pousser le next_cursor reçu de l'API et avancer d'une page. */
  next: (nextCursor: string | null) => void;
  /** Revenir à la page précédente. */
  prev: () => void;
  /** Reset à la première page (utile sur changement de filtres/search). */
  reset: () => void;
};

/**
 * Hook état pagination. Stack de cursors géré localement.
 * Pas persisté (URL/sessionStorage) — MVP : on accepte le reset au refresh.
 */
export function usePagination(): PaginationState {
  // stack[0] = null (page 0, première page).
  // stack[i] = cursor à utiliser pour atteindre la page i.
  const [stack, setStack] = useState<(string | null)[]>([null]);

  const cursor = stack[stack.length - 1] ?? null;
  const pageNumber = stack.length;
  const hasPrev = stack.length > 1;

  const next = useCallback((nextCursor: string | null) => {
    if (!nextCursor) return; // pas de page suivante
    setStack((s) => [...s, nextCursor]);
  }, []);

  const prev = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const reset = useCallback(() => {
    setStack([null]);
  }, []);

  return { cursor, pageNumber, hasPrev, next, prev, reset };
}

type Props = {
  pageNumber: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Désactivé pendant un fetch. */
  disabled?: boolean;
};

export function Pagination({ pageNumber, hasPrev, hasNext, onPrev, onNext, disabled }: Props) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t("pagination.aria")}
      className="flex items-center justify-between gap-3 py-3"
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={!hasPrev || disabled}
        className="btn-ghost"
      >
        ← {t("pagination.prev")}
      </button>
      <span className="text-sm text-slate-500">
        {t("pagination.page", { number: pageNumber })}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={!hasNext || disabled}
        className="btn-ghost"
      >
        {t("pagination.next")} →
      </button>
    </nav>
  );
}
